import css from './ProductList.module.css';
import ProductCard from '../../ProductCard/ProductCard';
import { FormattedMessage } from '../../../../util/reactIntl';
import React from 'react';
import NamedLink from '../../../NamedLink/NamedLink';
import { DesktopReviews } from '../../../../containers/ProfilePage/ProfilePage';
import { IconSpinner } from '../../../../components';
import { ReactComponent as ChevronLeft } from '../../../../assets/icons/chevron-left.svg';
import { ReactComponent as ChevronRight } from '../../../../assets/icons/chevron-right.svg';

const ProductList = ({
  listing,
  isActive = false,
  onCloseListing,
  onDiscardDraftListing,
  isFavoritesPage,
  isLandingPage,
  isLeague
}) => {
  if (!isActive) return null;

  // Listings for this section are still being fetched - show a spinner
  // instead of flashing the "no listings found" empty state, which was
  // misleading users into thinking a section had no inventory while data
  // was still loading.
  const isLoading = !!listing?.inProgress;
  if (isLoading && !listing?.listings?.length) {
    return (
      <div className={css.noListing}>
        <IconSpinner />
      </div>
    );
  }

  /* keep existing code - only fixed pagination source */
  const currentPage =
    Number(listing?.pagination?.page) ||
    Number(listing?.currentPage) ||
    1;

  const totalPages =
    Number(listing?.pagination?.totalPages) ||
    Number(listing?.totalPages) ||
    1;

  const totalItems =
    Number(listing?.pagination?.totalItems) ||
    Number(listing?.count) ||
    0;

  const hasPagination =
    !listing?.isReviews &&
    totalPages > 1;

  // -----------------------------------
  // SMART PAGE NUMBERS
  // -----------------------------------
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (currentPage <= 3) {
      pages.push(
        1,
        2,
        3,
        4,
        '...',
        totalPages
      );
    } else if (
      currentPage >= totalPages - 2
    ) {
      pages.push(
        1,
        '...',
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        '...',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        '...',
        totalPages
      );
    }

    return pages;
  };

  const handlePageChange = page => {
    if (
      page < 1 ||
      page > totalPages ||
      page === currentPage
    ) {
      return;
    }

    if (listing?.onPageChange) {
      listing.onPageChange(page);
    }
  };

  return (
    <>
      {/* EMPTY */}
      {totalItems <= 0 && (
        <div className={css.noListing}>
          <h3 className={css.noListingTitle}>
            <FormattedMessage
              id={
                listing.noListingsTitle
              }
            />
          </h3>

          <p className={css.noListingText}>
            <FormattedMessage
              id={
                listing.noListingsText
              }
            />
          </p>

          {!listing.isReviews && (
            <div className={css.noListingLink}>
              <NamedLink
                name="SearchPage"
                to={{ search: '?pub_for_sale_option=for_sale&scope=forSale&sort=createdAt' }}
                className={
                  css.primaryButton
                }
              >
                <FormattedMessage id="UserProfile.Jersies.Cta" />
              </NamedLink>
            </div>
          )}
        </div>
      )}

      {/* REVIEWS */}
      {listing.isReviews ? (
        <DesktopReviews
          reviews={listing.listings}
          queryReviewsError={
            listing.queryReviewsError
          }
          userTypeRoles={
            listing.userTypeRoles
          }
        />
      ) : (
        <div className={css.productsList}>
          {listing?.listings?.map(
            product => (
              <ProductCard
                key={`${product?.id?.uuid}-${product?.attributes?.state}`}
                product={product}
                onCloseListing={
                  onCloseListing
                }
                onDiscardDraftListing={
                  onDiscardDraftListing
                }
                isCurrentUser={
                  listing.isCurrentUser
                }
                isFavoritesPage={
                  isFavoritesPage
                }
                isLandingPage={
                  isLandingPage
                }
              />
            )
          )}
        </div>
      )}

      {/* PAGINATION */}
      {hasPagination && (
        <div className={css.pagination}>
          {/* PREV */}
          <button
            className={
              css.paginationButton
            }
            disabled={
              currentPage === 1
            }
            onClick={() =>
              handlePageChange(
                currentPage - 1
              )
            }
          >
            <ChevronLeft
              className={`${css.arrowIcon} ${currentPage === 1
                ? css.disabled
                : ''
                }`}
            />
          </button>

          {/* PAGE NUMBERS */}
          <div className={css.pageNumbers}>
            {getPageNumbers().map(
              (page, index) =>
                page === '...' ? (
                  <span
                    key={`dots-${index}`}
                    className={
                      css.ellipsis
                    }
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={page}
                    className={`${css.pageNumber} ${currentPage ===
                      page
                      ? css.activePage
                      : ''
                      }`}
                    onClick={() =>
                      handlePageChange(
                        page
                      )
                    }
                  >
                    {page}
                  </button>
                )
            )}
          </div>

          {/* NEXT */}
          <button
            className={
              css.paginationButton
            }
            disabled={
              currentPage ===
              totalPages
            }
            onClick={() =>
              handlePageChange(
                currentPage + 1
              )
            }
          >
            <ChevronRight
              className={`${css.arrowIcon} ${currentPage ===
                totalPages
                ? css.disabled
                : ''
                }`}
            />
          </button>
        </div>
      )}
    </>
  );
};

export default ProductList;